// import libraries
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";
// import files
import { env } from "../../env";
// import components
import Layout from "../../layouts/Layout";
import LoaderIcon from "../../components/Common/Icon/Loader";
import TransactionComponent from "../../components/Profile/TransactionComponent";
import { NavLink } from "react-router-dom";
import DateFilterDropdown from "../../components/Profile/Transaction/DateFilterDropdown ";

const CustomerView = () => {
    // variables
    const { t } = useTranslation();
    const { id } = useParams();
    const baseUrl = env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const activeTab = "border-b border-blue-normal text-blue-normal";
    const formatter = new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    // first
    const [showTab, setShowTab] = useState('transaction');
    // boolean
    const [isLoader, setIsLoader] = useState(true);
    // data
    const [rowCurrentPage, setCurrentPage] = useState(1);
    const [customerData, setCustomerData] = useState({});
    const [transactionList, setTransactionList] = useState([]);
    const [defaultList, setDefaultList] = useState([]);
    const [oldTransactionData, setOldTransactionData] = useState([]);
    const [lastCalculatedBalance, setLastCalculatedBalance] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            await getTransactionListApi(baseUrl, token);
            await getCustomerDataApi(baseUrl, token);
        };
        fetchData();
    }, [baseUrl, token]);

    // api's
    const getCustomerDataApi = async (url, token) => {
        await axios.post(url+"/api/Customer/GetCustomerDetail", {customerid: id},{ 
            headers: {
                "Content-Type" : "multipart/form-data",
                "Authorization" : `Bearer ${token}`
            }
        }).then((response) => {
            const customer = response.data.length ? response.data[0] : [];
            setCustomerData(customer);
            setIsLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getTransactionListApi = async(url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/Customer/GetCustomerTransactionList?customerid=${id}`,{ headers })
        .then((response) => {
            const sortedAsc = [...response.data].sort((a, b) =>
                moment(a.DocumentDate).isBefore(moment(b.DocumentDate)) ? -1 : 1
            );
            setDefaultList(sortedAsc);

            let previousBalance = 0;
            const transactionsWithBalanceAsc = sortedAsc.map(transaction => {
                const balance = previousBalance + (transaction?.Credit ? (transaction.Credit) : (transaction.Debit));
                previousBalance = balance;
                return { ...transaction, Balance: balance };
            });
            setLastCalculatedBalance(previousBalance);

            const transactionsWithBalanceDesc = transactionsWithBalanceAsc.reverse();
            setTransactionList(transactionsWithBalanceDesc);
            setOldTransactionData(transactionsWithBalanceDesc);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const openTab = (e, value) => {
        e.preventDefault();
        setShowTab(value);
    };
    const handleCurrentPage = (data) => {
        setCurrentPage(1);
    };
    const handleDateDataFromChild = (data) => {
        setCurrentPage(1);

        let previousBalance = 0;
        const transactionsWithBalanceAsc = defaultList.map(transaction => {
            const balance = previousBalance;
            previousBalance = balance + (transaction?.Credit ? (transaction.Credit) : (transaction.Debit));
            return { ...transaction, Balance: balance };
        });

        const fromDate = moment(data.fromDate, 'YYYY-MM-DD');
        const toDate = moment(data.toDate, 'YYYY-MM-DD');

        const filteredPrevious = transactionsWithBalanceAsc.filter(txn => {
            const txnDate = moment(txn.DocumentDate, 'YYYY-MM-DD');
            return txnDate.isSameOrAfter(fromDate, 'day') && txnDate.isSameOrBefore(toDate, 'day');
        });
        const filtered = oldTransactionData.filter(txn => {
            const txnDate = moment(txn.DocumentDate, 'YYYY-MM-DD');
            return txnDate.isSameOrAfter(fromDate, 'day') && txnDate.isSameOrBefore(toDate, 'day');
        });

        if(filteredPrevious.length > 0) {
            const addData = {
                "CustomerId": id,
                "Document": "",
                "DocumentDiscription": "previous_balance_check_added",
                "DocumentDate": "",
                "Credit": "",
                "Debit": "",
                "VATValue": "",
                "TotalValue": "",
                "Document_comment": "previous balance",
                "Balance": (filteredPrevious.length > 0) ? filteredPrevious[0].Balance : 0
            };
            const mergedArray = [...filtered, addData];
            setTransactionList(mergedArray);
        } else {
            setTransactionList(filtered);
        }
    };
    const clearFilter = () => {
        setTransactionList(oldTransactionData);
    };

    return (
        <div className="relative">
            <Layout>
                <div className="relative">
                    <div className="space-y-2 sm:space-y-4">
                        <div>
                            <h1 className="sm:text-2xl font-semibold capitalize">{ t('customer profile') }</h1>
                            <div className="text-light-400 text-xs sm:text-sm capitalize flex items-center"><NavLink className="cursor-pointer" to={'/customers'}>{ t('profile') }</NavLink>/{ t('customer profile') }</div>
                        </div>
                        { isLoader
                        ?
                            <LoaderIcon />
                        :
                        <div className="space-y-2 sm:space-y-4">
                            <div className="p-2 sm:p-4 bg-white border border-white rounded-lg space-y-4 shadow-around">
                                <div className="flex justify-between items-center">
                                    <div className="sm:space-y-2">
                                        <div>
                                            <h1 className="font-semibold lg:text-lg">{customerData.name}</h1>
                                        </div>
                                        <div className="font-normal text-xs lg:text-sm capitalize text-gray-dark">
                                            { t('customer code') }: <span>{customerData.code}</span>
                                        </div>
                                        {(customerData.address) && 
                                            <div className="hidden sm:flex items-center text-sm">
                                                <div className="flex">
                                                    <img src="../../../assets/icons/location.svg" alt="mail" />
                                                    <div className="ml-1 text-light-400">{customerData.address}</div>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    <div className="hidden sm:flex items-center space-x-6">
                                        <div className="flex items-center space-x-1">
                                            <h1 className="text-light-400 font-medium capitalize">{t ('balance')}: </h1>
                                            <div className="text-green-normal font-bold">{formatter.format(lastCalculatedBalance)} €</div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {/* <SortFilterDropdown sendSortDataFromChild={handleSortDataFromChild} /> */}
                                            <DateFilterDropdown sendDateDataFromChild={handleDateDataFromChild} sendCloseDataFromChild={clearFilter} />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between md:justify-normal space-x-3 md:space-x-6 overflow-auto no-scrollbar">
                                    <div onClick={(e) => openTab(e, 'transaction')} className={(showTab === "transaction" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('transaction') }</span></div>
                                </div>
                            </div>
                            {(showTab === "transaction") && <TransactionComponent transactionsList={transactionList} lastCalculatedBalance={lastCalculatedBalance} defaultData={defaultList} rowCurrentPageData={rowCurrentPage} sendCurrentPage={handleCurrentPage} oldTransactionData={oldTransactionData} />}
                        </div>
                    }
                    </div>
                </div>
            </Layout>
        </div>
    )
}

export default CustomerView;